import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit2, Trash2, X, DollarSign } from 'lucide-react';
import { icuRatesApi, locationsApi } from '@/api/client';
import { formatCurrency } from '@medical-counsellor/shared';
import { toast } from '@/utils/toast';

const icuRateSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional(),
    dailyRate: z.number().positive('Daily rate must be positive'),
    displayOrder: z.number().int().nonnegative().default(0),
});

type IcuRateFormData = z.infer<typeof icuRateSchema>;

export function IcuRatesPage() {
    const [selectedLocationId, setSelectedLocationId] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIcuRate, setEditingIcuRate] = useState<any>(null);
    const queryClient = useQueryClient();

    const { data: locations = [] } = useQuery({
        queryKey: ['locations'],
        queryFn: locationsApi.list,
    });

    const { data: icuRates = [], isLoading } = useQuery({
        queryKey: ['icu-rates', selectedLocationId],
        queryFn: () => icuRatesApi.list(selectedLocationId),
        enabled: !!selectedLocationId,
    });

    const createMutation = useMutation({
        mutationFn: (data: IcuRateFormData & { locationId: string }) =>
            icuRatesApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['icu-rates'] });
            toast.success('ICU rate created successfully');
            setIsModalOpen(false);
            setEditingIcuRate(null);
            reset({
                name: '',
                description: '',
                dailyRate: 0,
                displayOrder: 0,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to create ICU rate');
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<IcuRateFormData> }) =>
            icuRatesApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['icu-rates'] });
            toast.success('ICU rate updated successfully');
            setIsModalOpen(false);
            setEditingIcuRate(null);
            reset({
                name: '',
                description: '',
                dailyRate: 0,
                displayOrder: 0,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to update ICU rate');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: icuRatesApi.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['icu-rates'] });
            toast.success('ICU rate deleted successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to delete ICU rate');
        },
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<IcuRateFormData>({
        resolver: zodResolver(icuRateSchema),
    });

    const onSubmit = (data: IcuRateFormData) => {
        if (editingIcuRate) {
            updateMutation.mutate({ id: editingIcuRate.id, data });
        } else {
            createMutation.mutate({ ...data, locationId: selectedLocationId });
        }
    };

    const handleEdit = (icuRate: any) => {
        setEditingIcuRate(icuRate);
        reset(icuRate);
        setIsModalOpen(true);
    };

    // Auto-select first location if available
    if (locations.length > 0 && !selectedLocationId) {
        setSelectedLocationId(locations[0].id);
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900">ICU Rates</h1>
                <p className="text-slate-600">Manage ICU types and daily rates per location</p>
            </div>

            {/* Location Selector */}
            <div className="card">
                <div className="card-body">
                    <label className="label">Select Location</label>
                    <select
                        className="input max-w-md"
                        value={selectedLocationId}
                        onChange={(e) => setSelectedLocationId(e.target.value)}
                    >
                        <option value="">Choose a location...</option>
                        {locations.map((location: any) => (
                            <option key={location.id} value={location.id}>
                                {location.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {selectedLocationId && (
                <div className="card">
                    <div className="card-header flex items-center justify-between">
                        <h2 className="text-lg font-semibold">ICU Types</h2>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="btn btn-primary btn-sm"
                        >
                            <Plus className="w-4 h-4" />
                            Add ICU Type
                        </button>
                    </div>

                    <div className="card-body">
                        {isLoading ? (
                            <div className="space-y-2">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                ))}
                            </div>
                        ) : icuRates.length === 0 ? (
                            <div className="text-center py-8">
                                <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                                <p className="text-slate-500">No ICU types yet</p>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="btn btn-primary btn-sm mt-2"
                                >
                                    Add First ICU Type
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {icuRates.map((icuRate: any) => (
                                    <div
                                        key={icuRate.id}
                                        className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <h3 className="font-medium text-slate-900">{icuRate.name}</h3>
                                                {icuRate.description && (
                                                    <p className="text-sm text-slate-500 mt-1">{icuRate.description}</p>
                                                )}
                                                <div className="flex items-center gap-4 mt-2">
                                                    <div>
                                                        <span className="text-xs text-slate-500">Daily Rate</span>
                                                        <p className="text-lg font-semibold text-primary-600">
                                                            {formatCurrency(icuRate.dailyRate)}/day
                                                        </p>
                                                    </div>
                                                    {!icuRate.isActive && (
                                                        <span className="badge badge-secondary">Inactive</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => handleEdit(icuRate)}
                                                    className="p-2 hover:bg-slate-100 rounded"
                                                >
                                                    <Edit2 className="w-4 h-4 text-slate-500" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (confirm('Delete this ICU type?')) {
                                                            deleteMutation.mutate(icuRate.id);
                                                        }
                                                    }}
                                                    className="p-2 hover:bg-slate-100 rounded"
                                                >
                                                    <Trash2 className="w-4 h-4 text-error-500" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
                    <div className="card w-full max-w-md">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {editingIcuRate ? 'Edit ICU Type' : 'Add ICU Type'}
                            </h2>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setEditingIcuRate(null);
                                    reset({
                                        name: '',
                                        description: '',
                                        dailyRate: 0,
                                        displayOrder: 0,
                                    });
                                }}
                                className="p-1 hover:bg-slate-100 rounded"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="card-body space-y-4">
                                <div>
                                    <label className="label">ICU Type Name *</label>
                                    <input
                                        type="text"
                                        className={`input ${errors.name ? 'input-error' : ''}`}
                                        placeholder="e.g., General ICU, Cardiac ICU, NICU"
                                        {...register('name')}
                                    />
                                    {errors.name && (
                                        <p className="error-text">{errors.name.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="label">Description</label>
                                    <textarea
                                        className="input"
                                        rows={2}
                                        placeholder="Optional description"
                                        {...register('description')}
                                    />
                                </div>

                                <div>
                                    <label className="label">Daily Rate *</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={`input ${errors.dailyRate ? 'input-error' : ''}`}
                                        placeholder="e.g., 5000"
                                        {...register('dailyRate', { valueAsNumber: true })}
                                    />
                                    {errors.dailyRate && (
                                        <p className="error-text">{errors.dailyRate.message}</p>
                                    )}
                                    <p className="text-xs text-slate-500 mt-1">
                                        This rate will be used by default for all procedures
                                    </p>
                                </div>

                                <div>
                                    <label className="label">Display Order</label>
                                    <input
                                        type="number"
                                        className="input"
                                        placeholder="0"
                                        {...register('displayOrder', { valueAsNumber: true })}
                                    />
                                </div>
                            </div>

                            <div className="card-footer flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        setEditingIcuRate(null);
                                        reset({
                                            name: '',
                                            description: '',
                                            dailyRate: 0,
                                            displayOrder: 0,
                                        });
                                    }}
                                    className="btn btn-secondary btn-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createMutation.isPending || updateMutation.isPending}
                                    className="btn btn-primary btn-md"
                                >
                                    {createMutation.isPending || updateMutation.isPending
                                        ? 'Saving...'
                                        : editingIcuRate
                                            ? 'Update ICU Type'
                                            : 'Add ICU Type'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
